import { PermissionEnum } from '@Application/Security/Roles';
import DeleteRole from '@Application/Security/Roles/Commands/DeleteRole';
import UpdateRole from '@Application/Security/Roles/Commands/UpdateRole';
import deleteRole from '@Application/Security/Roles/deleteRole';
import updateRole from '@Application/Security/Roles/updateRole';
import FetchRoleImpactedUsers from '@Application/Users/Users/Queries/FetchRoleImpactedUsers';
import fetchRoleImpactedUsers from '@Application/Users/Users/fetchRoleImpactedUsers';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import permissionToRevokeSignal, {
	setPermissionToRevoke,
} from '@UI/Pages/Administration/Roles/Signals/permissionToRevokeSignal';
import roleToDeleteSignal, {
	setRoleToDelete,
} from '@UI/Pages/Administration/Roles/Signals/roleToDeleteSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { CACHE_DISABLED, useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';
import { ConfirmDeleteDialog, ConfirmDialog } from '@verclam/ui-kit';

const impactedUsersStyle = css`
	margin-top: 0.5rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

/**
 * The two confirmations shared by every branch of `RolesContent`: deleting a role and revoking a
 * permission from one carried by users. Reads everything from the store and from signals rather than
 * from props — `rolesList` and the permission catalogue are already kept fresh by `RolesContent`'s own
 * fetches, so this component only subscribes to them; the impacted-users preview is fetched here since
 * it is only ever consumed by this dialog.
 */
const RolesConfirmationDialogs: React.FC = () => {
	const dispatch = useDispatch();
	const { user: currentUser } = useStrictAuthenticatedUser();
	const accountId = currentUser.account?.id ?? null;
	const canReadUsers = useHasPermission(PermissionEnum.USER_READ);

	const roleToDeleteId = useSignalValue(roleToDeleteSignal);
	const permissionToRevoke = useSignalValue(permissionToRevokeSignal);

	const rolesList = useSelector((state) => state.rolesStore.rolesList);
	const catalog = useSelector((state) => state.permissionCatalogStore.catalog);

	const roleIdToPreview = permissionToRevoke?.roleId ?? null;

	// Preview-only: the confirmation always shows `assignedUserCount` regardless of this fetch, so a
	// failure here just leaves the nominative list empty rather than surfacing a toast —
	// `UsersNotifications` is never mounted on this page to display one. Its own `usersStore` location
	// (`roleImpactedUsers`, written only by `ROLE_IMPACTED_USERS_FETCHED`) keeps this read from ever
	// overwriting the Users screen's own list.
	const { data: impactedUsersList } = useStoreQuery({
		selector: (state) => state.usersStore.roleImpactedUsers,
		stopOn: (value) =>
			roleIdToPreview === null ||
			accountId === null ||
			!canReadUsers ||
			(value !== null && value.accountId === accountId && value.roleId === roleIdToPreview),
		query: () => {
			if (roleIdToPreview === null || accountId === null || !canReadUsers) {
				return Promise.resolve();
			}

			return dispatch(fetchRoleImpactedUsers(new FetchRoleImpactedUsers(accountId, roleIdToPreview)));
		},
		deps: [accountId, canReadUsers, roleIdToPreview],
		cache: CACHE_DISABLED,
	});

	// Guards against showing a still-cached previous role's names while the request for the newly
	// selected role is in flight.
	const impactedUsers =
		roleIdToPreview !== null && impactedUsersList !== null && impactedUsersList.roleId === roleIdToPreview
			? impactedUsersList.list
			: null;

	const roleToDelete = rolesList?.findById(roleToDeleteId) ?? null;

	const roleToRevokeFrom = React.useMemo(
		() => (permissionToRevoke !== null ? (rolesList?.findById(permissionToRevoke.roleId) ?? null) : null),
		[permissionToRevoke, rolesList],
	);

	const permissionToRevokeLabel = React.useMemo(() => {
		if (permissionToRevoke === null) {
			return '';
		}

		return (
			catalog?.list.find((permission) => permission.value === permissionToRevoke.permission)?.label ??
			permissionToRevoke.permission
		);
	}, [permissionToRevoke, catalog]);

	const handleDeleteDialogHide = React.useCallback(() => {
		setRoleToDelete(null);
	}, []);

	const handleDeleteAccept = React.useCallback(() => {
		if (accountId === null || roleToDeleteId === null) {
			return;
		}

		// No explicit reselection here: once the refetch this triggers resolves, `RolesContent`'s
		// selection effect sees the deleted id is gone from the fresh list and falls back to the new
		// first role.
		dispatch(deleteRole(new DeleteRole(accountId, roleToDeleteId))).catch(ignoreHandledError);
		// Close immediately: `acceptCallback` never triggers `onHide` on `ConfirmDeleteDialog`, and
		// waiting for the request to settle would leave the dialog open long enough for a second click.
		setRoleToDelete(null);
	}, [accountId, roleToDeleteId, dispatch]);

	const handleRevokeDialogHide = React.useCallback(() => {
		setPermissionToRevoke(null);
	}, []);

	const handleRevokeAccept = React.useCallback(() => {
		if (accountId === null || permissionToRevoke === null || roleToRevokeFrom === null) {
			return;
		}

		const nextPermissions = roleToRevokeFrom.permissions.filter(
			(value) => value !== permissionToRevoke.permission,
		);

		dispatch(
			updateRole(
				new UpdateRole(
					accountId,
					roleToRevokeFrom.id,
					roleToRevokeFrom.label,
					roleToRevokeFrom.description,
					nextPermissions as readonly PermissionEnum[],
				),
			),
		).catch(ignoreHandledError);
	}, [accountId, permissionToRevoke, roleToRevokeFrom, dispatch]);

	return (
		<React.Fragment>
			<ConfirmDeleteDialog
				visible={roleToDeleteId !== null}
				onHide={handleDeleteDialogHide}
				header="Supprimer le rôle"
				message={
					<React.Fragment>
						Cette action est irréversible. Confirmer la suppression du rôle{' '}
						<strong>{roleToDelete?.label ?? ''}</strong> ?
					</React.Fragment>
				}
				confirmText={roleToDelete?.label ?? ''}
				acceptCallback={handleDeleteAccept}
			/>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={permissionToRevoke !== null}
				onHide={handleRevokeDialogHide}
				header="Retirer la permission"
				message={
					<React.Fragment>
						Retirer « {permissionToRevokeLabel} » du rôle{' '}
						<strong>{roleToRevokeFrom?.label ?? ''}</strong> ?{' '}
						{roleToRevokeFrom?.assignedUserCount ?? 0} compte(s) portent ce rôle et perdront ce
						droit.
						{impactedUsers !== null && impactedUsers.length > 0 ? (
							<div css={impactedUsersStyle}>
								{impactedUsers.map((impactedUser) => impactedUser.fullName).join(', ')}
							</div>
						) : null}
					</React.Fragment>
				}
				acceptLabel="Retirer"
				acceptSeverity="danger"
				accept={handleRevokeAccept}
			/>
		</React.Fragment>
	);
};

export default RolesConfirmationDialogs;
